import type { CSSProperties, FC, ReactNode } from 'react'
import type { PricingPlan } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonLink } from '@/components/generals/button'
import { AnimatedNumber } from '@/components/generals/animated-number'

type Billing = 'monthly' | 'annual'

interface PricingPlansProps {
    plans: Array<PricingPlan>
    compareLink?: boolean
    reveal?: boolean
    className?: string
}

const priceFor = (plan: PricingPlan, billing: Billing): number | null => {
    if (billing === 'annual') return plan.annualPrice ?? plan.monthlyPrice

    return plan.monthlyPrice ?? plan.annualPrice
}

const savingsPercent = (plans: Array<PricingPlan>): number | null => {
    const comparable = plans.find(plan => plan.monthlyPrice && plan.annualPrice && plan.annualPrice < plan.monthlyPrice)

    if (!comparable?.monthlyPrice || !comparable.annualPrice) return null

    return Math.round((1 - comparable.annualPrice / comparable.monthlyPrice) * 100)
}

const withBilling = (href: string, billing: Billing): string => {
    if (!href.startsWith('/signup')) return href

    return `${href}${href.includes('?') ? '&' : '?'}billing=${billing}`
}

export const PricingPlans: FC<PricingPlansProps> = ({ plans, compareLink = true, reveal = true, className }): ReactNode => {
    const [billing, setBilling] = useState<Billing>('annual')
    const savings = savingsPercent(plans)

    if (!plans.length) {
        return (
            <p className='rounded-2xl border border-burgundy/10 bg-ivory p-8 text-center text-stone-dark'>
                Pricing is being updated. <Link href='/contact' className='font-medium text-burgundy underline underline-offset-4'>Talk to our team</Link> for a quote.
            </p>
        )
    }

    return (
        <div className={className}>
            <div className='flex flex-wrap items-center justify-center gap-3'>
                <div role='group' aria-label='Billing period' className='inline-flex rounded-full border border-burgundy/15 bg-ivory p-1'>
                    {(['monthly', 'annual'] as Array<Billing>).map(option => (
                        <button
                            key={option}
                            type='button'
                            aria-pressed={billing === option}
                            onClick={() => setBilling(option)}
                            className={cn(
                                'rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300',
                                billing === option ? 'bg-burgundy text-cream' : 'text-stone-dark hover:text-burgundy'
                            )}
                        >
                            {option === 'monthly' ? 'Monthly' : 'Annual'}
                        </button>
                    ))}
                </div>

                {savings !== null && (
                    <span className='rounded-full bg-gold/25 px-3 py-1 text-xs font-medium text-gold-dark'>
                        Save {savings}% with annual billing
                    </span>
                )}
            </div>

            <div className='mt-12 grid overflow-hidden rounded-[28px] border border-burgundy/10 bg-ivory lg:grid-cols-3'>
                {plans.map((plan, index) => {
                    const price = priceFor(plan, billing)
                    const dark = plan.highlighted

                    return (
                        <div
                            key={plan.id}
                            data-reveal={reveal ? '' : undefined}
                            className={cn(
                                'relative flex flex-col p-8 md:p-10',
                                dark ? 'bg-burgundy text-cream' : 'text-burgundy',
                                index > 0 && !dark && !plans[index - 1]?.highlighted && 'border-t border-burgundy/10 lg:border-l lg:border-t-0'
                            )}
                            style={{ '--reveal-delay': `${index * 100}ms` } as CSSProperties}
                        >
                            <div className='flex items-center justify-between gap-3'>
                                <h3 className='font-heading text-2xl font-medium tracking-[-0.02em]'>{plan.name}</h3>

                                {dark && (
                                    <span className='rounded-full bg-gold px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-burgundy'>
                                        Most popular
                                    </span>
                                )}
                            </div>

                            {plan.tagline && (
                                <p className={cn('mt-3 min-h-[3rem] text-sm leading-relaxed', dark ? 'text-cream/70' : 'text-stone-dark')}>
                                    {plan.tagline}
                                </p>
                            )}

                            <div className='mt-8 flex items-end gap-2'>
                                {price !== null ? (
                                    <>
                                        <span className='font-heading text-[3.25rem] font-medium leading-none tracking-[-0.04em]'>
                                            <AnimatedNumber value={price} prefix='$' />
                                        </span>

                                        <span className={cn('pb-1.5 text-sm', dark ? 'text-cream/60' : 'text-stone')}>/ month</span>
                                    </>
                                ) : (
                                    <span className='font-heading text-[3.25rem] font-medium leading-none tracking-[-0.04em]'>
                                        {plan.customPriceLabel ?? 'Custom'}
                                    </span>
                                )}
                            </div>

                            <p className={cn('mt-3 text-xs', dark ? 'text-cream/55' : 'text-stone')}>
                                {price !== null
                                    ? (billing === 'annual' ? 'Billed annually' : 'Billed monthly, cancel anytime')
                                    : (plan.billingNote ?? 'Tailored to your organization')}
                            </p>

                            <ButtonLink
                                href={withBilling(plan.ctaHref, billing)}
                                variant={dark ? 'light' : plan.monthlyPrice === null ? 'secondary' : 'primary'}
                                size='lg'
                                arrow
                                className='mt-8 w-full'
                            >
                                {plan.ctaLabel}
                            </ButtonLink>

                            <ul className={cn('mt-10 space-y-3.5 border-t pt-8', dark ? 'border-cream/10' : 'border-burgundy/10')}>
                                {plan.features.map(feature => (
                                    <li key={feature} className={cn('flex items-start gap-3 text-sm', dark ? 'text-cream/85' : 'text-stone-dark')}>
                                        <Check aria-hidden='true' className={cn('mt-0.5 size-4 shrink-0', dark ? 'text-gold' : 'text-gold-dark')} />
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                })}
            </div>

            <div className='mt-8 flex flex-col items-center justify-between gap-4 text-sm text-stone-dark md:flex-row'>
                <p>Every plan includes bank-grade encryption, read-only connections and unlimited historical data.</p>

                {compareLink && (
                    <Link href='/pricing#compare' className='group inline-flex items-center gap-1.5 font-medium text-burgundy'>
                        Compare all features
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />
                    </Link>
                )}
            </div>
        </div>
    )
}
